import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Button, Modal, Select } from "antd";
import { FaCoins } from "react-icons/fa";
import {
  ADDABLE_MARKETS,
  loadExtraSymbols,
  removeExtraSymbol,
  saveExtraSymbol,
} from "./extraSymbols";
import styles from "./AddBoardSymbol.module.css";
import { stockDisplayName } from "./stockBoards";
import { pivotApiUrl } from "../redux/live-suggestion/liveSuggestionSlice";
const ASSISTANT_API = "https://hav8w1xk8h.execute-api.ap-southeast-2.amazonaws.com/dev/api";

const FALLBACK = {
  forex: ["EURUSD=X", "GBPUSD=X", "USDJPY=X", "AUDUSD=X", "USDCAD=X"],
  us: ["AAPL", "MSFT", "NVDA", "AMZN", "GOOGL", "META", "TSLA", "BRK-B", "JPM", "XOM", "AVGO", "AMD", "CVX", "UNH", "PG", "GLD", "USO", "IBIT", "SPY", "TLT"],
  indonesia: ["BBCA.JK", "BBRI.JK", "BMRI.JK", "TLKM.JK", "ASII.JK"],
  iran: ["فملی", "فولاد", "شپنا", "خودرو", "وبملت", "شستا", "فارس", "کگل", "کچاد", "وتجارت"],
  australia: ["BHP.AX", "CBA.AX", "CSL.AX", "NAB.AX", "WBC.AX"],
  japan: ["7203.T", "6758.T", "8306.T", "9984.T", "8035.T", "6861.T"],
  ksa: ["2222.SR", "1120.SR", "1180.SR", "2010.SR", "7010.SR", "1211.SR"],
};

function shown(symbol) {
  return stockDisplayName(symbol);
}

function readSymbols(payload) {
  let data = payload;
  if (data && typeof data.body === "string") {
    try {
      data = JSON.parse(data.body);
    } catch (error) {
      data = {};
    }
  }
  return Array.isArray(data?.symbols) ? data.symbols : [];
}

function fallbackSymbols(market, query) {
  const needle = String(query || "").trim().toLowerCase();
  return (FALLBACK[market] || [])
    .filter((symbol) => !needle || symbol.toLowerCase().includes(needle) || shown(symbol).toLowerCase().includes(needle))
    .map((symbol) => ({ symbol, label: shown(symbol) }));
}

function AddBoardSymbol({ market, existing = [], onChange }) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [picked, setPicked] = useState(undefined);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [tick, setTick] = useState(0);
  const searchTimer = useRef(null);
  const listedMarket = ADDABLE_MARKETS.includes(market);

  const loadList = (query) => {
    setLoading(true);
    const finish = (symbols) => {
      const rows = symbols.length ? symbols : fallbackSymbols(market, query);
      setOptions(rows);
      setNote(rows.length ? "" : "The symbol list did not load.");
      setLoading(false);
    };
    axios
      .post(
        pivotApiUrl({ action: "list_market_symbols", market, q: query || "" }),
        { action: "list_market_symbols", market, q: query || "" },
        { timeout: 12000 },
      )
      .then((res) => {
        const symbols = readSymbols(res?.data);
        if (symbols.length) {
          finish(symbols);
          return null;
        }
        return axios.post(
          ASSISTANT_API,
          { platform_mode: "trade_assistant", request_type: "list_market_symbols", market, q: query || "" },
          { timeout: 12000 },
        );
      })
      .then((res) => {
        if (!res) return;
        finish(readSymbols(res?.data));
      })
      .catch(() => finish([]));
  };

  useEffect(() => {
    if (!open || !listedMarket) return undefined;
    loadList("");
    return () => clearTimeout(searchTimer.current);
  }, [open, market, listedMarket]);

  if (!listedMarket) return null;
  const extras = loadExtraSymbols(market);

  const addSymbol = () => {
    const listed = options.some((item) => item.symbol === picked);
    if (!listed) {
      setNote("Choose a symbol from the list.");
      return;
    }
    const saved = saveExtraSymbol(market, picked, existing);
    if (!saved) {
      setNote("Choose a symbol from the list.");
      return;
    }
    setPicked(undefined);
    setNote("");
    setTick((value) => value + 1);
    onChange();
  };

  return (
    <>
      <button type="button" className={styles.card} onClick={() => setOpen(true)}>
        <h3>
          <FaCoins />
          Choose any Symbol
        </h3>
        <p>Add new symbol from this exchange to see Pivot model suggestion!</p>
      </button>
      <Modal
        open={open}
        title="Choose any Symbol"
        onCancel={() => setOpen(false)}
        footer={null}
        destroyOnClose
      >
        <form
          className={styles.form}
          onSubmit={(event) => {
            event.preventDefault();
            addSymbol();
          }}
        >
          <Select
            className={styles.pick}
            showSearch
            value={picked}
            placeholder="Choose a listed symbol"
            options={options.map((item) => ({
              value: item.symbol,
              label: shown(item.symbol) !== String(item.symbol || "").replace(/=X$/i, "")
                ? shown(item.symbol)
                : (item.label || shown(item.symbol)),
            }))}
            onChange={setPicked}
            onSearch={(text) => {
              clearTimeout(searchTimer.current);
              searchTimer.current = setTimeout(() => loadList(text), 400);
            }}
            filterOption={false}
            loading={loading}
            notFoundContent={loading ? "Loading…" : "No symbols"}
            aria-label="Listed symbol"
          />
          <Button type="primary" htmlType="submit" disabled={!picked}>
            Add symbol
          </Button>
        </form>
        {note ? <p className={styles.note}>{note}</p> : null}
        {extras.length ? (
          <div className={styles.chips} data-tick={tick}>
            {extras.map((symbol) => (
              <button
                key={symbol}
                type="button"
                className={styles.chip}
                onClick={() => {
                  removeExtraSymbol(market, symbol);
                  setTick((value) => value + 1);
                  onChange();
                }}
              >
                {shown(symbol)} ×
              </button>
            ))}
          </div>
        ) : null}
      </Modal>
    </>
  );
}

export default AddBoardSymbol;
